import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../layout/mainLayout';
import {
  Typography,
  Box,
  Stack,
  Divider,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CloseIcon from '@mui/icons-material/Close';
import { SearchNormal } from 'iconsax-react';
import ApiClient from '../../config/api';
import TabOne from './components/tabOne';
import TabTwo from './components/tabTwo';
import ChangeStatusConfirm from './components/changeStatusConfirm';
import DetailModal from './components/detailModal';
import RejectParcel from './components/rejectParcel';
import GetParcel from './components/getParcel';
import { setIsLoading, setDutyActive } from '../../store/globalSlice';
import { useDispatch, useSelector } from 'react-redux';
import getLocation from '../../components/getLocation';
import sendLog from '../../components/sendLog';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && <Box sx={{ width: '100%' }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Missions = () => {
  const dispatch = useDispatch();
  const api = ApiClient();
  const dutyActive = useSelector((st) => st?.global?.dutyActive);
  const [data, setData] = useState([{ items: [] }]);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [value, setValue] = useState(0);
  const [missionNo, setMissionNo] = useState(0);
  const [openDetail, setOpenDetail] = useState(false);
  const [openGetParcel, setOpenGetParcel] = useState('');
  const [openChangeStatusConfirm, setOpenChangeStatusConfirm] = useState('');
  const [location, setLocation] = useState({
    coords: {
      latitude: 0,
      longitude: 0,
      altitude: 0,
      accuracy: 0,
      altitudeAccuracy: 0,
      heading: 0,
      speed: 0
    },
    timestamp: 0
  });
  const [openRejectedModal, setOpenRejectedModal] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [pullY, setPullY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStartY = useRef(0);
  const isPulling = useRef(false);
  const pullYRef = useRef(0);
  const isRefreshingRef = useRef(false);

  const handleDutyChange = (event) => {
    const nextActive = event.target.checked;
    dispatch(setDutyActive(nextActive));
    if (!nextActive) {
      setOpenDetail(false);
      setOpenGetParcel('');
      setOpenChangeStatusConfirm('');
      setOpenRejectedModal(false);
    }
  };

  const parseMessageData = (data) => {
    if (typeof data !== 'string') return data;

    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  };

  const handleLocationChanged = (nextLocation) => {
    setLocation(nextLocation);
  };

  const onRecievedMessage = (event) => {
    const message = parseMessageData(event?.data);

    if (message?.type == 'location') {
      handleLocationChanged(message?.data)
    }
    sendLog(message?.data)
  };

  useEffect(function () {
    window.addEventListener('message', onRecievedMessage);

    return function () {
      window.removeEventListener('message', onRecievedMessage);
    };
  });

  useEffect(() => {
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "denied") {
          console.error("Geolocation access denied. Please enable location permissions.");
          return;
        }
        requestLocation();
      });
    } else {
      requestLocation();
    }
    getLocation()
  }, []);

  const requestLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleLocationChanged({
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              altitude: 0,
              accuracy: 0,
              altitudeAccuracy: 0,
              heading: 0,
              speed: position.coords.speed || 0
            },
            timestamp: 0
          });
        },
        (error) => {
          console.error("Geolocation error: ", error);
        },
        {
          enableHighAccuracy: false,
          timeout: 20000,
          maximumAge: 0,
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const getData = async ({ silent = false } = {}) => {
    try {
      if (!silent) dispatch(setIsLoading(true));
      const response = await api.get(`/api/app/v1/shipito/tasks`);
      setData(response.data.data);
    } catch (error) {
      console.error(
        'Error fetching tracking app sliders:',
        error.message || error
      );
    } finally {
      if (!silent) dispatch(setIsLoading(false));
    }
  };

  const getDataRef = useRef(getData);
  getDataRef.current = getData;

  useEffect(() => {
    if (!dutyActive) return;
    if (openDetail == false) {
      getData();
    }
  }, [openDetail, dutyActive]);

  useEffect(() => {
    const PULL_THRESHOLD = 56;
    const PULL_MAX = 96;
    let wheelTimer = 0;

    const getScroller = () => document.getElementById('main-scroll');

    const shouldIgnore = (event) => {
      const target = event.target;
      if (!target || !target.closest) return true;
      if (target.closest('.mapboxgl-map')) return true;
      if (target.closest('input, textarea, [contenteditable="true"]')) return true;
      return false;
    };

    const setDistance = (next) => {
      const clamped = Math.max(0, Math.min(next, PULL_MAX));
      pullYRef.current = clamped;
      setPullY(clamped);
    };

    const refresh = async () => {
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      isPulling.current = false;
      setIsRefreshing(true);
      setDistance(48);
      try {
        await getDataRef.current({ silent: true });
      } finally {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
        setDistance(0);
      }
    };

    const finishPull = () => {
      if (isRefreshingRef.current) return;
      isPulling.current = false;
      if (pullYRef.current >= PULL_THRESHOLD) {
        refresh();
      } else {
        setDistance(0);
      }
    };

    const onTouchStart = (event) => {
      if (isRefreshingRef.current || shouldIgnore(event)) {
        isPulling.current = false;
        return;
      }
      pullStartY.current = event.touches[0].clientY;
      isPulling.current = true;
    };

    const onTouchMove = (event) => {
      if (!isPulling.current || isRefreshingRef.current || shouldIgnore(event)) {
        return;
      }
      const scroller = getScroller();
      if (!scroller) return;

      const currentY = event.touches[0].clientY;
      const dy = currentY - pullStartY.current;

      if (scroller.scrollTop <= 1 && dy > 0) {
        event.preventDefault();
        event.stopPropagation();
        setDistance(dy * 0.42);
      } else if (scroller.scrollTop > 1) {
        pullStartY.current = currentY;
        if (pullYRef.current !== 0) setDistance(0);
      }
    };

    const onTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      if (pullYRef.current > 0) finishPull();
    };

    const onWheel = (event) => {
      if (isRefreshingRef.current || shouldIgnore(event)) return;
      const scroller = getScroller();
      if (!scroller || scroller.scrollTop > 1) return;
      if (event.deltaY >= 0) return;

      event.preventDefault();
      setDistance(pullYRef.current + Math.min(-event.deltaY, 24) * 0.45);
      window.clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(finishPull, 160);
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true });
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });

    return () => {
      window.clearTimeout(wheelTimer);
      document.removeEventListener('touchstart', onTouchStart, true);
      document.removeEventListener('touchmove', onTouchMove, true);
      document.removeEventListener('touchend', onTouchEnd, true);
      document.removeEventListener('touchcancel', onTouchEnd, true);
      document.removeEventListener('wheel', onWheel, true);
    };
  }, []);

  return (
    <MainLayout>
      <ChangeStatusConfirm
        open={openChangeStatusConfirm}
        setOpen={setOpenChangeStatusConfirm}
        setOpenGetParcel={setOpenGetParcel}
        selectedParcel={selectedParcel}
        selectedTask={selectedTask}
        setOpenRejectedModal={setOpenRejectedModal}
        setSelectedStatus={setSelectedStatus}
      />
      <DetailModal
        openDetail={openDetail}
        setOpenDetail={setOpenDetail}
        selectedData={selectedData}
        setOpenGetParcel={setOpenGetParcel}
        setOpenChangeStatusConfirm={setOpenChangeStatusConfirm}
        missionNo={missionNo}
        selectedTask={selectedTask}
        setSelectedParcel={setSelectedParcel}
        selectedTaskDetail={selectedTaskDetail}
      />
      <GetParcel
        openGetParcel={openGetParcel}
        setOpenGetParcel={setOpenGetParcel}
        selectedTask={selectedTask}
        selectedParcel={selectedParcel}
        selectedStatus={selectedStatus}
        setOpenDetail={setOpenDetail}
        location={location}
      />
      <RejectParcel
        selectedParcel={selectedParcel}
        openGetParcel={openRejectedModal}
        selectedStatus={selectedStatus}
        selectedTask={selectedTask}
        setOpenGetParcel={setOpenRejectedModal}
        setOpenDetail={setOpenDetail}
        location={location}
      />
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          width: '100%',
        }}
      >
        <Box
          sx={{
            height: isRefreshing ? 52 : pullY,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            transition: isRefreshing || pullY === 0 ? 'height 0.2s ease' : 'none',
          }}
        >
          <CircularProgress
            size={22}
            variant={isRefreshing || pullY >= 56 ? 'indeterminate' : 'determinate'}
            value={Math.min((pullY / 56) * 100, 100)}
          />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            paddingRight: '20px',
            paddingLeft: '20px',
            marginTop: '20px',
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" sx={{ gap: 1, minWidth: 0 }}>
            <Typography fontSize={20} fontWeight={600} textAlign={'left'}>
              ماموریت‌ها
            </Typography>
            {dutyActive ? (
              <FormControlLabel
                sx={{ mr: 0, ml: 0 }}
                control={
                  <Switch
                    checked
                    onChange={handleDutyChange}
                    color="success"
                    sx={{
                      transform: 'scale(1.35)',
                      transformOrigin: 'center',
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#12a000',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#12a000',
                        opacity: 1,
                      },
                      '& .MuiSwitch-track': {
                        opacity: 1,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    fontSize={15}
                    fontWeight={800}
                    color="success.main"
                    sx={{ mr: 0.5 }}
                  >
                    فعال
                  </Typography>
                }
              />
            ) : null}
          </Stack>
          {dutyActive ? (
          <ToggleButtonGroup
            exclusive
            size="large"
            value={viewMode}
            onChange={(_, nextView) => {
              if (nextView) setViewMode(nextView);
            }}
            aria-label="تغییر نمایش ماموریت‌ها"
            sx={{
              bgcolor: 'background.muted',
              borderRadius: '10px',
              '& .MuiToggleButton-root': {
                border: 0,
                px: 1.25,
                py: 0.5,
                color: 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                },
              },
            }}
          >
            <ToggleButton value="list" aria-label="نمایش لیست">
              <FormatListBulletedIcon sx={{ fontSize: 18, mr: 0.5 }} />
              فهرست
            </ToggleButton>
            <ToggleButton value="map" aria-label="نمایش نقشه">
              <MapOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
              نقشه
            </ToggleButton>
          </ToggleButtonGroup>
          ) : null}
        </Stack>
        {dutyActive ? (
        <Box sx={{ paddingRight: '20px', paddingLeft: '20px', mt: 1.5, mb: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="جستجو نام، شماره یا آدرس"
            inputProps={{ 'aria-label': 'جستجو در ماموریت‌ها' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchNormal size="18" color="#A1A1AA" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="پاک کردن جستجو"
                    onClick={() => setSearchQuery('')}
                  >
                    <CloseIcon sx={{ fontSize: 18, color: '#A1A1AA' }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: 'background.muted',
                color: 'text.primary',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>
        ) : null}
        {dutyActive ? (
        <Divider
          sx={{
            width: '100%',
          }}
        />
        ) : null}
        {/* <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          sx={{
            width: '100%',
            borderBottom: '1px solid #0000001f',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Tab
            label="جمع‌آوری"
            sx={{ width: '50%', fontSize: '14px' }}
            {...a11yProps(0)}
          />
          <Tab
            label="توزیع"
            sx={{ width: '50%', fontSize: '14px' }}
            {...a11yProps(1)}
          />
        </Tabs> */}
      </Stack>
      {dutyActive ? (
        <>
          <CustomTabPanel value={value} index={0}>
            <TabOne
              setOpenDetail={setOpenDetail}
              setSelectedData={setSelectedData}
              data={data}
              setSelectedTask={setSelectedTask}
              setMissionNo={setMissionNo}
              setSelectedTaskDetail={setSelectedTaskDetail}
              setOpenChangeStatusConfirm={setOpenChangeStatusConfirm}
              getData={getData}
              location={location}
              viewMode={viewMode}
              searchQuery={searchQuery}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <TabTwo />
          </CustomTabPanel>
        </>
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 'calc(100dvh - 180px)',
            px: '24px',
            textAlign: 'center',
          }}
        >
          <Typography fontSize={22} fontWeight={800} mb={0.5}>
            وضعیت شما غیرفعال است
          </Typography>
          <Switch
            checked={false}
            onChange={handleDutyChange}
            color="success"
            sx={{
              my: 3,
              transform: 'scale(2.4)',
              transformOrigin: 'center',
              '& .MuiSwitch-thumb': {
                width: 22,
                height: 22,
                boxShadow: '0 2px 8px rgba(17,17,17,0.25)',
              },
              '& .MuiSwitch-track': {
                borderRadius: 14,
                opacity: 1,
                backgroundColor: '#A1A1AA',
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#12a000',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#12a000',
                opacity: 1,
              },
            }}
          />
          <Typography fontSize={16} fontWeight={700} color="text.secondary">
            برای مشاهده ماموریت‌ها و ارسال موقعیت، وضعیت را فعال کنید
          </Typography>
        </Stack>
      )}
    </MainLayout>
  );
};

export default Missions;
